// Ficha de um jogador: quem é (personagem dos bots ou a guilda, para humanos), a
// vocação e como está indo na partida. Abre ao clicar num jogador da lista.
import { controlsRegion, type PlayerView } from '@estandarte/engine';
import { CHARACTERS, guildOf, regionName, territoryName, theme, vocationName } from '../theme';
import { useUi } from '../store/ui';
import { Icon } from '../ui/Icon';
import { CardBack } from './Cards';
import { Crest, Portrait } from './Panels';

/** Cabeçalho e história, sem dados da partida (serve também na tela inicial). */
export function CharacterSheet({
  name,
  color,
  vocation,
  bot,
  you,
}: {
  name: string;
  color: string;
  vocation?: string | null;
  bot: boolean;
  you?: boolean;
}) {
  const ch = bot ? CHARACTERS[name] : undefined;
  const guild = guildOf(theme, color);
  const voc = vocation ? theme.vocations[vocation as keyof typeof theme.vocations] : null;
  return (
    <div className="char-sheet">
      <div className="char-head" style={{ ['--guild' as string]: guild.hex }}>
        <Crest color={color} size={54} />
        <div className="char-id">
          <div className="char-name">{name}</div>
          <div className="char-epithet">{ch ? ch.epithet : you ? 'você, à frente da guilda' : 'jogador humano'}</div>
          <div className="char-meta sc">
            {guild.name}
            {ch && !ch.epithet.includes(territoryName(ch.origin)) ? ` · de ${territoryName(ch.origin)}` : ''}
          </div>
        </div>
        {voc && <Portrait vocation={vocation!} size={54} />}
      </div>
      {ch ? (
        <>
          <p className="char-story">{ch.story}</p>
          <blockquote className="char-quote">“{ch.quote}”</blockquote>
        </>
      ) : (
        <p className="char-story">
          {you ? 'Você comanda' : `${name} comanda`} a {guild.name}
          {guild.world ? `, de ${guild.world}` : ''}. {guild.flavor}
        </p>
      )}
      {voc && (
        <div className="char-voc">
          <span className="sc">
            {voc.name} · {voc.role}
          </span>
          <span>{voc.text}</span>
        </div>
      )}
      {ch && (
        <div className="char-guild hint">
          <strong>{guild.name}</strong>
          {guild.world ? ` (${guild.world})` : ''}: {guild.flavor}
        </div>
      )}
    </div>
  );
}

/** Marcas de era ao lado do nome: caveira e cabeça a prêmio. */
export function PlayerMarks({ view, id }: { view: PlayerView; id: number }) {
  const skull = view.skulls.includes(id);
  const bounty = view.info.ruleset.bounty && view.bounty === id;
  if (!skull && !bounty) return null;
  return (
    <span className="pl-marks">
      {bounty && (
        <span className="mark bounty" title="Cabeça a prêmio: a primeira conquista contra ele em cada turno vale 1 carta">
          <Icon name="crown" size={14} />
        </span>
      )}
      {skull && (
        <span className="mark skull" title="Caveira vermelha: se perder um território, descarta 1 carta e 1 loot">
          <Icon name="skull" size={14} />
        </span>
      )}
    </span>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="char-stat">
      <span className="num">{value}</span>
      <span className="sc">{label}</span>
    </div>
  );
}

/** Ficha aberta durante a partida, com os números do jogador agora. */
export function PlayerBio({ view, me }: { view: PlayerView; me: number | null }) {
  const { bio, setBio } = useUi();
  if (bio === null) return null;
  const p = view.players[bio];
  if (!p) return null;
  const terrs = Object.values(view.territories).filter((t) => t.owner === p.id);
  const troops = terrs.reduce((n, t) => n + t.troops, 0);
  const regions = view.info.content.map.regions.filter((r) => controlsRegion(view, p.id, r.id)).map((r) => regionName(r.id));
  const vocations = view.info.ruleset.addons.vocations;
  return (
    <div className="hud-card bio-card" role="dialog" aria-label={`Ficha de ${p.name}`}>
      <button className="btn small ghost icon-btn bio-close" onClick={() => setBio(null)} aria-label="Fechar ficha">
        <Icon name="close" />
      </button>
      <CharacterSheet name={p.name} color={p.color} vocation={vocations ? p.vocation : null} bot={p.kind === 'bot'} you={p.id === me} />
      <div className="char-now">
        <div className="char-marks">
          <PlayerMarks view={view} id={p.id} />
          {view.skulls.includes(p.id) && <span className="hint">Com a caveira vermelha até o próximo turno.</span>}
          {view.info.ruleset.bounty && view.bounty === p.id && <span className="hint">Cabeça a prêmio nesta rodada.</span>}
        </div>
        <div className="sc char-now-title">
          Na partida{!p.alive ? ' · eliminado' : ''}
          {p.kind === 'bot' ? ` · bot ${p.difficulty === 'easy' ? 'fácil' : 'normal'}` : ''}
        </div>
        <div className="char-stats">
          <Stat label="territórios" value={terrs.length} />
          <Stat label="tropas" value={troops} />
          <Stat
            label="cartas"
            value={
              <>
                <CardBack deck="territory" mini /> {p.cardCount}
              </>
            }
          />
          <Stat
            label="loot"
            value={
              <>
                <CardBack deck="loot" mini /> {p.lootCount}
              </>
            }
          />
          <Stat label="bosses" value={p.bossKills} />
          {p.vocation === 'harmony' && vocations && <Stat label="harmonia" value={`${p.harmony}/${view.info.ruleset.harmonyMax}`} />}
        </div>
        <div className="hint">{regions.length ? `Domina: ${regions.join(', ')}.` : 'Ainda não domina nenhuma região inteira.'}</div>
      </div>
    </div>
  );
}
